import { Box } from '@mui/material';
import {
    backendServices,
    Button,
    Dialog,
    isBuyerPortalFeatureEnabled,
    MultiStepForm,
    RecursivePartial,
    useBuyerPortalSettings,
    useCountries,
    useCurrency,
    useQueryParams,
} from 'common';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useCompanyPublicData, useGetCompanyUserProfile } from '../../../services';
import { routes } from '../../../signup-portal.routes';
import { SupplierSignUpSchema } from './SupplierSignUpSchema';
import { AccountAndContact, YourBusiness, YourCustomer } from './SupplierSignUpSteps';
import { supplierUserProfileVMToSupplierSignUpData } from './supplierUserProfileVMToSupplierSignUpData';
import { extractAPIErrorMessage, mapApiErrorToDisplayMsg, usePostSupplierCompany } from './usePostSupplierCompany';
import { usePutSupplierCompany } from './usePutSupplierCompany';
import { useVatArchiveService } from './useVatArchiveService';

type UpdateSupplierLocationState = {
    buyerCompanyId: number;
    supplierCompanyId: number;
};

type CreateSupplierLocationState = {
    submissionMethod:
        | backendServices.ViewModels.SubmissionMethod
        | backendServices.ViewModels.SubmissionMethodViewModel;
};

type SupplierSignUpLocationState =
    | RecursivePartial<CreateSupplierLocationState>
    | RecursivePartial<UpdateSupplierLocationState>;

function useSupplierSignUpPageQueryParams() {
    const params = useQueryParams<{ regCode?: string }>();

    return useMemo(() => {
        const regCode = params.regCode;

        const isValidRegCode = typeof regCode === 'string' && regCode.length;

        return {
            regCode: isValidRegCode ? regCode : null,
        };
    }, [params]);
}

// TODO: Add comments to document each step in this hook
function useRouteData() {
    const { companyToken = '' } = useParams<{
        companyToken?: string;
    }>();

    const { regCode } = useSupplierSignUpPageQueryParams();

    const history = useHistory();
    const location = useLocation<SupplierSignUpLocationState | null>();

    // * Not null when the form is used to update an existing supplier company profile
    const supplierCompanyProfileParams = useMemo<UpdateSupplierLocationState | null>(() => {
        if (location.state == null) {
            return null;
        }
        if (!('supplierCompanyId' in location.state) || !('buyerCompanyId' in location.state)) {
            return null;
        }

        const { buyerCompanyId, supplierCompanyId } = location.state;

        if (typeof buyerCompanyId !== 'number' || typeof supplierCompanyId !== 'number') {
            return null;
        }

        return {
            buyerCompanyId,
            supplierCompanyId,
        };
    }, [location.state]);

    // * Not null when the form is used to create a new supplier company profile
    const submissionMethod = useMemo<Pick<
        backendServices.ViewModels.SubmissionMethodViewModel,
        'Id' | 'Name' | 'SubmissionMethod'
    > | null>(() => {
        if (location.state == null || !('submissionMethod' in location.state)) {
            return null;
        }

        const method = location.state?.submissionMethod;

        if (!['number', 'object'].includes(typeof method) || method == null) {
            return null;
        }

        if (typeof method === 'number') {
            if (method !== backendServices.ViewModels.SubmissionMethod.WebEntry) {
                return null;
            }

            /**
             * ! Figure out what to do when the invoice threshold isn't exceeded.
             */
            return {
                Id: 0,
                Name: 'Web Entry',
                SubmissionMethod: backendServices.ViewModels.SubmissionMethod.WebEntry,
            };
        }

        return {
            Id: method.Id,
            Name: method.Name,
            SubmissionMethod: method.SubmissionMethod,
        };
    }, [location.state]);

    const goToLandingPage = useCallback(() => {
        const searchParams = regCode ? new URLSearchParams({ regCode }) : null;
        history.replace(
            routes.supplierLandingPage.path
                .replace(':companyToken', companyToken)
                .concat(searchParams ? `?${searchParams}` : '')
        );
    }, [history, companyToken, regCode]);

    useEffect(() => {
        if (submissionMethod == null && supplierCompanyProfileParams == null) {
            goToLandingPage();
        }
    }, [submissionMethod, goToLandingPage, supplierCompanyProfileParams]);

    return { companyToken, submissionMethod: submissionMethod, goToLandingPage, supplierCompanyProfileParams };
}

export function SupplierSignUp() {
    const { goToLandingPage, companyToken, submissionMethod, supplierCompanyProfileParams } = useRouteData();

    const history = useHistory();

    const {
        data: supplierCompanyUserProfile,
        isLoading: isLoadingSupplierUserProfile,
        isFetching: isFetchingSupplierUserProfile,
    } = useGetCompanyUserProfile({
        buyerCompanyId: supplierCompanyProfileParams?.buyerCompanyId,
        supplierCompanyId: supplierCompanyProfileParams?.supplierCompanyId,
    });

    const isLoadingForm =
        !!supplierCompanyProfileParams && (isLoadingSupplierUserProfile || isFetchingSupplierUserProfile);

    const formValues = useMemo(() => {
        if (supplierCompanyUserProfile == null) {
            return undefined;
        }

        return supplierUserProfileVMToSupplierSignUpData(supplierCompanyUserProfile);
    }, [supplierCompanyUserProfile]);

    /**
     * * This ensures that we start fetching the data as soon as the form is rendered
     * * to avoid loading spinners when the users gets to the step that uses the data.
     */
    useCountries();
    useCurrency();
    const { data: company } = useCompanyPublicData({ search: companyToken });
    const { data: buyerPortalSettings } = useBuyerPortalSettings({ companyId: company?.ID ?? null });
    useVatArchiveService({
        companyId: company?.ID,
    });

    const submissionMethodKey =
        submissionMethod != null ? backendServices.ViewModels.SubmissionMethod[submissionMethod.SubmissionMethod] : '';

    const lastSubmittedCredentials = useRef<{ loginEmail: string; password: string } | null>(null);

    const {
        mutate: postSupplierCompany,
        isLoading: isSubmitting,
        isError: submissionFailed,
        error: submissionError,
        isSuccess: submissionSucceeded,
    } = usePostSupplierCompany({
        searchToken: companyToken,
        onSuccess: (result) => {
            const skipPostSignup = !isBuyerPortalFeatureEnabled(buyerPortalSettings, 'EnableSupplierPostSignup');

            if (!skipPostSignup && lastSubmittedCredentials.current && result) {
                history.push(routes.supplierPostSignup.path.replaceAll(':companyToken', companyToken), {
                    loginEmail: lastSubmittedCredentials.current.loginEmail,
                    password: lastSubmittedCredentials.current.password,
                    companyId: result.CompanyId,
                    companyName: result.CompanyName,
                    vendorId: result.VendorId,
                    buyerCompanyId: company?.ID,
                    submissionMethodKey,
                });
                lastSubmittedCredentials.current = null;
                return;
            }

            history.push(
                routes.supplierInvoiceSubmissionGuide.path
                    .replaceAll(':companyToken', companyToken)
                    .replaceAll(':submissionMethod', submissionMethodKey)
            );
        },
    });

    const [showSuccessModal, setShowSuccessModal] = useState(false);

    const {
        mutate: putSupplierCompany,
        isLoading: isSubmittingUpdate,
        isError: updateSubmissionFailed,
        error: updateSubmissionError,
        isSuccess: updateSubmissionSucceeded,
        data: supplierUpdateResult,
    } = usePutSupplierCompany({
        onSuccess() {
            setShowSuccessModal(true);
        },
    });

    const redirectToSupplierPortalLogin = () => {
        setShowSuccessModal(false);
        window.location.replace('https://supplier1.transcepta.com/SupplierPortal/login');
    };

    const apiErrorMsg = extractAPIErrorMessage(submissionError);

    const updateSubmissionErrorMessage = ((updateSubmissionError as any)?.response?.data?.errors?.[0]?.Message ??
        null) as string | null;

    const errorMessage = mapApiErrorToDisplayMsg(
        apiErrorMsg ?? updateSubmissionErrorMessage ?? 'There was an error while trying to submit your changes.'
    );

    const isCloudCapture = supplierCompanyProfileParams != null;

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <Dialog
                title="Success"
                content={
                    supplierUpdateResult?.CompanyName.length
                        ? `Supplier Company "${supplierUpdateResult.CompanyName}" has been updated successfully.`
                        : 'Supplier Company has been updated successfully.'
                }
                open={showSuccessModal}
                handleOnClose={redirectToSupplierPortalLogin}
                actions={<Button onClick={redirectToSupplierPortalLogin}>OK</Button>}
            />
            <MultiStepForm
                containerSx={{
                    p: {
                        xs: 0,
                        sm: 4,
                    },
                    minHeight: 0,
                    width: '100%',
                }}
                schema={SupplierSignUpSchema}
                values={formValues}
                isLoading={isLoadingForm}
                formAlertProps={{
                    errorMessage: submissionFailed || updateSubmissionFailed ? errorMessage : null,
                    loadingMessage: isLoadingForm ? 'Loading...' : null,
                    savingMessage: isSubmitting || isSubmittingUpdate ? 'Saving...' : null,
                    successMessage:
                        submissionSucceeded || updateSubmissionSucceeded ? 'Changes submitted successfully' : null,
                    warningMessage: null,
                }}
                submitHandler={(formData) => {
                    if (isCloudCapture) {
                        putSupplierCompany({
                            supplierFormData: formData,
                            supplierCompanyUserProfile: supplierCompanyUserProfile!,
                        });
                        return;
                    }
                    lastSubmittedCredentials.current = {
                        loginEmail: formData.accountAndContact.loginEmail,
                        password: formData.accountAndContact.password,
                    };
                    postSupplierCompany({ supplierData: formData, submissionMethod: submissionMethod! });
                }}
                shouldDisableNext={(ctx) => {
                    if (!ctx.isLastStep) {
                        return false;
                    }

                    return isSubmitting || submissionSucceeded || isSubmittingUpdate || updateSubmissionSucceeded;
                }}
                onCancel={(reset) => {
                    reset();
                    goToLandingPage();
                }}
                steps={[
                    {
                        id: 1,
                        title: 'Create Account',
                        component: AccountAndContact,
                        fields: ['accountAndContact'],
                    },
                    {
                        id: 2,
                        title: 'Your Customer',
                        component: (props) => (
                            <YourCustomer
                                // eslint-disable-next-line react/jsx-props-no-spreading
                                {...props}
                                companyToken={companyToken}
                                submissionMethod={submissionMethod}
                                isCloudCapture={isCloudCapture}
                            />
                        ),
                        fields: ['customer'],
                    },
                    {
                        id: 3,
                        title: 'Your Business',
                        component: YourBusiness,
                        fields: ['business'],
                    },
                ]}
            />
        </Box>
    );
}
